// @file Swipe to navigate between slideshow frames; the swipe axis follows the slideshow
// forward/back buttons (vertical when the nav shows the up/down controls).
import { trackEvent } from '@@/bits/analytics'
import { ActionType } from '@@/bits/whiteboard'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardModeStore } from '@@/pinia/whiteboard_mode'
import { useWhiteboardSlideshowVerticalNav } from '@@/vuecomposables/useWhiteboardSlideshowVerticalNav'
import { useWhiteboardActions } from '@@/vuecomposables/use_whiteboard_actions'
import { useEventListener } from '@vueuse/core'
import { toValue, type MaybeRefOrGetter } from 'vue'

const SWIPE_THRESHOLD_PX = 50

function useWhiteboardSlideshowSwipeNavigation(options: {
  /** Swipes starting inside this element are ignored (e.g. the nav bar owns its own taps). */
  ignoreTarget?: MaybeRefOrGetter<Element | null | undefined>
}): void {
  const whiteboardStore = useWhiteboardStore()
  const whiteboardModeStore = useWhiteboardModeStore()
  const { handleAction } = useWhiteboardActions()
  const xVerticalNavControls = useWhiteboardSlideshowVerticalNav()

  let touchStartX = 0
  let touchStartY = 0
  let touchStartTarget: EventTarget | null = null
  let isSwipeValid = false

  const isEnabled = (): boolean => whiteboardModeStore.isSlideshowMode && !whiteboardStore.isFrameBreakout

  const handleSwipeStart = (e: TouchEvent): void => {
    if (!isEnabled()) return
    // A second finger (e.g. pinch) invalidates the gesture so it can't trigger frame nav.
    isSwipeValid = e.touches.length === 1
    if (!isSwipeValid) return
    touchStartX = e.changedTouches[0].clientX
    touchStartY = e.changedTouches[0].clientY
    touchStartTarget = e.target
  }

  const handleSwipeEnd = (e: TouchEvent): void => {
    if (!isEnabled() || !isSwipeValid) return
    isSwipeValid = false
    if (touchStartTarget instanceof Element && toValue(options.ignoreTarget)?.contains(touchStartTarget) === true) {
      return
    }
    const deltaX = e.changedTouches[0].clientX - touchStartX
    const deltaY = e.changedTouches[0].clientY - touchStartY
    const axisDelta = xVerticalNavControls.value ? deltaY : deltaX
    const crossAxisDelta = xVerticalNavControls.value ? deltaX : deltaY
    // Negative delta (left or up) advances, matching the forward button.
    if (Math.abs(axisDelta) < SWIPE_THRESHOLD_PX || Math.abs(axisDelta) <= Math.abs(crossAxisDelta)) return
    if (axisDelta < 0) {
      trackEvent('Whiteboard', 'Swiped to next page', null, null, { context: 'slideshow' })
      void handleAction(ActionType.GoToNextPage)
    } else {
      trackEvent('Whiteboard', 'Swiped to previous page', null, null, { context: 'slideshow' })
      void handleAction(ActionType.GoToPreviousPage)
    }
  }

  useEventListener(window, 'touchstart', handleSwipeStart, { passive: true })
  useEventListener(window, 'touchend', handleSwipeEnd, { passive: true })
}

export { useWhiteboardSlideshowSwipeNavigation }
